import type { SampleContent } from "./catalog";
import {
  SAMPLE_LOCALES,
  sampleCopyFamily,
  sampleFallbackCopy,
  type SampleLocale,
} from "./material-copy";
import { translateSampleName } from "./sample-names";

/**
 * Public text for a material or finish sample.
 *
 * Many stored sample dossiers were filled with template sentences about the
 * photos ("Echt materiaalmonster van X. De foto's tonen ...", "kies het
 * gewenste gramgewicht bij het product") instead of telling the customer what
 * the material is. This resolver removes those sentences and, where nothing
 * substantial is left, uses the curated material copy from material-copy.ts.
 * Stored text that really describes the material still wins, so later edits in
 * the admin keep working.
 */

// Sentences (or clauses after a semicolon) about the photos, the physical
// sample or "choose the weight on the product page". Not material information.
const FILLER_PATTERNS: RegExp[] = [
  // nl ("foto- en displaypanelen" or "fotoprints" are uses, not filler)
  /\b(de|deze|die|op de|vier|echte|apart[e]?) foto['’]?s?\b/i,
  /\bfoto['’]s (tonen|laten|zijn)\b/i,
  /gefotografeerd/i,
  /proef(vel|vellen|materiaal|bedrukking|druk)/i,
  /(kleur|materiaal)monster/i,
  /\becht(e)? monster\b/i,
  /\bdetailfoto\b/i,
  /\bclose-?up\b/i,
  /\bvergroting\b/i,
  /gramgewicht/i,
  /bij het product\b/i,
  // en
  /\b(the|these|this|four|each) photo(graph)?s? (show|shows|let|lets|display|displays)\b/i,
  /\b(in|on) the photo(graph)?s?\b/i,
  /\bphotographed\b/i,
  /\breal (colour |color )?sample\b/i,
  /\btest print\b/i,
  /\benlarged\b/i,
  /\bproduct page\b/i,
  // de
  /\b(die|das|diese|dieses|vier) fotos? (zeig|lass)/i,
  /\bauf (dem|den) fotos?\b/i,
  /fotografiert/i,
  /\bechte[sn]? (material|farb)?muster\b/i,
  /\b(material|farb)muster\b/i,
  /probedruck/i,
  /nahaufnahme/i,
  /vergrößert/i,
  // fr
  /\b(les|la|cette|ces|quatre) photos? (montr|présent|permet|laiss)/i,
  /\bsur (la|les) photos?\b/i,
  /photographiée?s?\b/i,
  /échantillon (réel|de couleur)/i,
  /\bvrai échantillon\b/i,
  /\bgros plan\b/i,
  /\bagrandi/i,
  // instructions to look, compare or check the product
  /^(bekijk|vergelijk|controleer|compare|view|check|look at|vergleiche|sieh|prüfe|beachte|comparez|regardez|vérifiez|consultez)\b/i,
];

// Supplier names never appear in customer-facing copy.
export const SUPPLIER_NAME_PATTERN =
  /\b(print\.com|probo|gelato|prodigi|printful|plakkers|helloprint|drukwerkdeal|vistaprint|flyeralarm|midocean|pf concept|sendcards|onlineprinters|saxoprint|wir machen druck)\b/i;

// Phrases that must never survive in a rendered description.
export const OLD_FILLER_PHRASES: RegExp[] = [
  /gefotografeerde variant/i,
  /kies het gewenste gramgewicht/i,
  /materiaalmonster toont/i,
  /\becht(e)? (materiaal)?monster van\b/i,
  /\bde foto['’]s tonen\b/i,
  /\bdeze foto['’]s tonen\b/i,
  /\breal sample of\b/i,
  /\bthe photos show\b/i,
  /\becht(es)? (material)?muster von\b/i,
  /\bdie fotos zeigen\b/i,
  /échantillon réel de\b/i,
  /\bles photos montrent\b/i,
];

// "De leverancier noemt ..." or "volgens de leverancier": we do not talk
// about suppliers on the site, named or not.
const SUPPLIER_SENTENCE =
  /^(de leverancier|the supplier|der lieferant|le fournisseur)\b/i;
const SUPPLIER_PHRASE =
  / ?(volgens (de )?leverancier|according to the supplier|laut (dem )?lieferanten?|selon le fournisseur)(?=[ ,.;])/gi;

const isFiller = (part: string) => FILLER_PATTERNS.some((p) => p.test(part.trim()));

function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-ÖØ-Þ0-9"'“‘(])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Drops photo/sample filler and supplier sentences, keeps the rest verbatim. */
export function stripSampleFiller(text: string | undefined | null): string {
  if (!text) return "";
  return text
    .split(/\n\s*\n/)
    .map((paragraph) =>
      splitSentences(paragraph.replace(/\s+/g, " ").trim())
        .flatMap((sentence) => {
          if (SUPPLIER_NAME_PATTERN.test(sentence)) return [];
          if (SUPPLIER_SENTENCE.test(sentence)) return [];
          sentence = sentence.replace(SUPPLIER_PHRASE, "");
          const clauses = sentence.split(/;\s+/);
          const kept = clauses.filter((c) => !isFiller(c));
          if (!kept.length) return [];
          if (kept.length === clauses.length) return [sentence];
          let joined = kept.join("; ").trim();
          if (!/[.!?]$/.test(joined)) joined += ".";
          return [joined[0].toUpperCase() + joined.slice(1)];
        })
        .join(" "),
    )
    .filter(Boolean)
    .join("\n\n")
    .replace(/[\u2013\u2014]/g, "-")
    .trim();
}

const sentenceCount = (text: string) => splitSentences(text).length;

function numbers(value: string, unit: RegExp): number[] {
  if (!unit.test(value)) return [];
  return [
    ...new Set(
      (value.match(/\d+(?:[.,]\d+)?/g) || [])
        .map((n) => Number(n.replace(",", ".")))
        .filter((n) => Number.isFinite(n) && n > 0),
    ),
  ].sort((a, b) => a - b);
}

const fmt = (n: number, locale: SampleLocale) =>
  locale === "en" ? String(n) : String(n).replace(".", ",");

const RANGE_WORDS: Record<SampleLocale, { to: string; product: string; weight: string; thickness: string; sep: string }> = {
  nl: { to: "tot", product: "afhankelijk van het product", weight: "Gewicht", thickness: "Dikte", sep: ": " },
  en: { to: "to", product: "depending on the product", weight: "Weight", thickness: "Thickness", sep: ": " },
  de: { to: "bis", product: "je nach Produkt", weight: "Grammatur", thickness: "Stärke", sep: ": " },
  fr: { to: "à", product: "selon le produit", weight: "Grammage", thickness: "Épaisseur", sep: " : " },
};

/** "Gewicht: 90 tot 400 g/m², afhankelijk van het product." from the specs. */
export function sampleSizeLine(
  specs: SampleContent["specs"],
  locale: SampleLocale,
): string {
  const w = RANGE_WORDS[locale];
  const lines: string[] = [];
  const weight = specs.find((s) => /^gewicht$/i.test(s.label));
  const thickness = specs.find((s) => /^dikte$/i.test(s.label));
  const describe = (values: number[], unit: string, label: string) => {
    if (!values.length) return;
    const first = fmt(values[0], locale);
    const last = fmt(values[values.length - 1], locale);
    lines.push(
      values.length === 1
        ? `${label}${w.sep}${first} ${unit}.`
        : `${label}${w.sep}${first} ${w.to} ${last} ${unit}, ${w.product}.`,
    );
  };
  if (weight) describe(numbers(weight.value, /g\s*\/\s*m/i), "g/m²", w.weight);
  if (thickness) describe(numbers(thickness.value, /mm/i), "mm", w.thickness);
  return lines.join(" ");
}

type Translation = SampleContent["translations"]["nl"];

function resolveLocale(
  stored: Translation | undefined,
  locale: SampleLocale,
  entry: { name: string; kind: string },
  specs: SampleContent["specs"],
): Translation {
  const family = sampleCopyFamily(entry.name, entry.kind);
  // Stored en/de/fr names are mostly copies of the Dutch name.
  const storedName = stored?.name?.trim() || entry.name;
  const name =
    locale === "nl" ? storedName : translateSampleName(storedName, locale);
  const copy = (family?.copy || sampleFallbackCopy(name, entry.kind))[locale];
  const storedDescription = stripSampleFiller(stored?.description);
  const storedIntro = stripSampleFiller(stored?.intro);
  const sizeLine = sampleSizeLine(specs, locale);

  const storedIsSubstantial =
    sentenceCount(storedDescription) >= 2 &&
    storedDescription.length >= Math.min(120, Math.round(copy.description.length * 0.6));
  let description = storedIsSubstantial ? storedDescription : copy.description;
  if (!storedIsSubstantial && sizeLine && !/g\/m²|\bmm\b/.test(description))
    description = `${description} ${sizeLine}`;

  const introIsSubstantial =
    storedIntro.length >= 20 &&
    storedIntro.toLowerCase() !== name.toLowerCase() &&
    storedIntro.toLowerCase() !== `${name.toLowerCase()}.`;
  // A single specific stored sentence ("Foliedruk regenboog laat ... van
  // kleur veranderen") is a better opening than the family's general intro.
  const storedFirst = splitSentences(storedDescription)[0] || "";
  const intro = introIsSubstantial
    ? storedIntro
    : storedIsSubstantial || storedFirst.length >= 30
      ? storedFirst
      : copy.intro;

  return {
    name,
    intro,
    description,
    applications: stripSampleFiller(stored?.applications) || copy.applications,
    care: stripSampleFiller(stored?.care),
  };
}

/** Replaces template filler with real material copy in all four languages. */
export function resolveSampleContent(
  content: SampleContent,
  entry: { name: string; kind: string },
): SampleContent {
  const translations = {} as SampleContent["translations"];
  // Niet elke aanroeper geeft een naam mee (option-visuals kent alleen de
  // opgeslagen vertalingen); dan geldt de Nederlandse naam uit de inhoud.
  const named = {
    ...entry,
    name: entry.name || content.translations?.nl?.name || "",
  };
  for (const locale of SAMPLE_LOCALES)
    translations[locale] = resolveLocale(
      content.translations?.[locale],
      locale,
      named,
      content.specs,
    );
  return { ...content, translations };
}
